"use client";

// src/primitives/content.tsx
import * as React3 from "react";
import { Container } from "@stepstone-genesis/components-container";
import { Dialog as DialogBody } from "@stepstone-genesis/ariakit-library";
import { useStyleConfig, useTheme } from "@stepstone-genesis/system";
import { Motion as Motion2 } from "@stepstone-genesis/components-motion";
import { MotionContext } from "@stepstone-genesis/animation-library";
import { omitProps, toArray } from "@stepstone-genesis/utilities";
import { useActiveBreakpointValue } from "@stepstone-genesis/hooks-use-active-breakpoint-value";

// src/primitives/context.tsx
import * as React from "react";
import {
  DialogProvider as BaseDialogProvider,
  useDialogContext
} from "@stepstone-genesis/ariakit-library";
import { useUID } from "@stepstone-genesis/uid-library";

// src/primitives/hooks.ts
import {
  useDialogStore
} from "@stepstone-genesis/ariakit-library";
function useDialog(options) {
  const { onDismiss, setOpen, ...storeOptions } = options || {};
  const dialog = useDialogStore({
    animated: true,
    setOpen: (open) => {
      if (!open && onDismiss) {
        onDismiss();
      }
      setOpen == null ? void 0 : setOpen(open);
    },
    ...storeOptions
  });
  return dialog;
}

// src/primitives/context.tsx
var CustomDialogContext = React.createContext(null);
var useCustomDialogContext = () => {
  const context = React.useContext(CustomDialogContext);
  if (!context) {
    throw new Error(
      "useCustomDialogContext must be used within DialogProvider"
    );
  }
  return context;
};
var DialogProvider = ({
  onDismiss,
  id: identifier,
  isDismissible = true,
  isOpen = false,
  children,
  a11yLabel = "change me",
  renderOverlay = true,
  overlayVariant = "dark",
  isOverlayAnimatable = true,
  isContentAnimatable = true,
  isSticky = false,
  hideOnOverlayClick = isDismissible != null ? isDismissible : true,
  size = ["small", "medium", "large"],
  variant = "default",
  autoFocus = false,
  autoFocusOnShow = true,
  autoFocusOnHide = true,
  hideOnEscape = true,
  focusable = true,
  ...initialState
}) => {
  const uuid = useUID();
  const id = identifier != null ? identifier : `genesis-dialog-${uuid}`;
  const [open, setOpen] = React.useState(isOpen);
  const dialog = useDialog({ onDismiss, open, setOpen });
  const getPersistentElements = React.useCallback(() => {
    var _a, _b;
    const toasts = Array.from(document.querySelectorAll("toast"));
    const gdprConsentModal = document.querySelector(
      "#GDPRConsentManagerContainer"
    );
    const fromProps = (_b = (_a = initialState == null ? void 0 : initialState.getPersistentElements) == null ? void 0 : _a.call(initialState)) != null ? _b : [];
    return [
      ...toasts,
      ...gdprConsentModal ? [gdprConsentModal] : [],
      ...fromProps
    ];
  }, [initialState]);
  const originalState = React.useMemo(
    () => ({
      ...dialog,
      open: isOpen
    }),
    [dialog, isOpen]
  );
  React.useEffect(() => {
    setOpen(isOpen);
  }, [setOpen, isOpen]);
  const customState = React.useMemo(
    () => ({
      isDismissible,
      isOverlayAnimatable,
      isContentAnimatable,
      isSticky,
      hideOnOverlayClick,
      renderOverlay,
      overlayVariant,
      size,
      variant,
      a11yLabel,
      id,
      onDismiss,
      autoFocus,
      autoFocusOnShow,
      autoFocusOnHide,
      hideOnEscape,
      focusable,
      getPersistentElements,
      ...initialState
    }),
    [
      isDismissible,
      isOverlayAnimatable,
      isContentAnimatable,
      isSticky,
      hideOnOverlayClick,
      renderOverlay,
      overlayVariant,
      size,
      variant,
      a11yLabel,
      id,
      onDismiss,
      autoFocus,
      autoFocusOnShow,
      autoFocusOnHide,
      hideOnEscape,
      focusable,
      getPersistentElements,
      initialState
    ]
  );
  return /* @__PURE__ */ React.createElement(BaseDialogProvider, { ...originalState }, /* @__PURE__ */ React.createElement(CustomDialogContext.Provider, { value: customState }, children));
};

// src/styles.ts
var overlay = {
  position: "fixed",
  top: "spaceNone",
  right: "spaceNone",
  bottom: "spaceNone",
  left: "spaceNone",
  display: "flex",
  justifyContent: "center",
  overflowY: "auto",
  width: "100%",
  zIndex: "zIndex80",
  overscrollBehaviorY: "contain"
};
var containerRoot = {
  position: "absolute",
  width: "100%",
  maxHeight: "100%",
  zIndex: "zIndex100",
  _focusVisible: {
    outline: "none"
  },
  _focus: {
    outline: "none"
  }
};
var contentContainer = {
  default: {
    root: {
      width: "100%",
      margin: "auto"
    },
    small: {
      maxWidth: "400px"
    },
    medium: {
      maxWidth: "560px"
    },
    large: {
      maxWidth: "800px"
    },
    xl: {
      maxWidth: "1152px"
    }
  }
};
var dismissButton = {
  size: "componentDialogSize",
  color: "colorBlack",
  _hover: {
    color: "colorBlack"
  },
  _active: {
    color: "colorBlack",
    _focus: {
      color: "colorBlack"
    }
  },
  _focus: {
    _hover: {
      color: "colorBlack"
    }
  }
};
var styles = {
  containerRoot,
  contentContainer,
  overlay,
  dismissButton
};

// src/animations.ts
var content = {
  hidden: {
    opacity: 0,
    y: "-100%",
    x: "0"
  },
  visible: {
    opacity: 1,
    y: "0",
    transition: {
      delay: 0.2
    }
  }
};
var contentFromBottom = {
  hidden: {
    opacity: 0,
    y: "100%",
    x: "0"
  },
  visible: {
    opacity: 1,
    y: "0",
    transition: {
      delay: 0.2,
      bounce: 0
    }
  }
};
var overlay2 = {
  visible: {
    opacity: 1
  },
  hidden: {
    opacity: 0
  }
};
var animations = {
  content,
  contentFromBottom,
  overlay: overlay2
};

// src/primitives/overlay.tsx
import * as React2 from "react";
import { AnimatePresence, Motion } from "@stepstone-genesis/components-motion";
import { Base } from "@stepstone-genesis/system";
var overlayColors = {
  dark: "rgba(0, 0, 0, 0.65);",
  light: "rgba(255, 255, 255, 0.65);",
  transparent: "rgba(255, 255, 255, 0);"
};
var DialogOverlay = ({ children, ...rest }) => {
  const context = useDialogContext();
  const { isOverlayAnimatable, overlayVariant, renderOverlay } = useCustomDialogContext();
  const ctxState = context == null ? void 0 : context.useState();
  const animateOverlay = React2.useMemo(() => {
    return isOverlayAnimatable ? {
      variants: animations.overlay,
      initial: "hidden",
      animate: "visible",
      exit: "hidden"
    } : {};
  }, [isOverlayAnimatable]);
  const shouldShowOverlay = renderOverlay || overlayVariant !== "transparent";
  return shouldShowOverlay ? /* @__PURE__ */ React2.createElement(Base, { ...context, ...rest }, /* @__PURE__ */ React2.createElement(AnimatePresence, null, (ctxState == null ? void 0 : ctxState.open) && /* @__PURE__ */ React2.createElement(
    Motion,
    {
      ...animateOverlay,
      __root: {
        ...styles.overlay,
        // FIXME: this will be switched to a token using a new color lighten/darken function
        backgroundColor: overlayVariant && overlayColors[overlayVariant]
      },
      element: "DRAWER_OVERLAY"
    },
    children
  ))) : /* @__PURE__ */ React2.createElement(AnimatePresence, null, (ctxState == null ? void 0 : ctxState.open) ? /* @__PURE__ */ React2.createElement(
    Motion,
    {
      __root: {
        ...styles.overlay
      },
      element: "DRAWER_OVERLAY"
    },
    children
  ) : null);
};

// src/primitives/content.tsx
var DialogContent = ({ children, ...rest }) => {
  var _a, _b, _c, _d, _e, _f;
  const context = useDialogContext();
  const { theme } = useTheme();
  const customContext = useCustomDialogContext();
  const omittedCustomContext = omitProps(customContext, ["onDismiss"]);
  const ctxState = context == null ? void 0 : context.useState();
  const { DIALOG_CONTAINER } = useStyleConfig("DIALOG_CONTAINER", {
    styles: {
      base: {
        ...styles.containerRoot
      },
      responsiveVariants: [
        {
          id: "dialog-container-sticky",
          prop: customContext.isSticky,
          variants: {
            true: {
              bottom: "spaceNone",
              paddingX: "spaceNone"
            },
            false: {
              bottom: "auto"
            }
          }
        }
      ]
    },
    props: { isSticky: (_a = customContext.isSticky) != null ? _a : false }
  });
  const { DIALOG_CONTENT } = useStyleConfig("DIALOG_CONTENT", {
    styles: {
      base: {
        /* This is required, because the way motion animates elements is slightly different in newer version
           Need this fix to align close button within container
         */
        position: "relative",
        paddingTop: [
          `${(_b = theme.sizes) == null ? void 0 : _b.size60}`,
          `${(_c = theme.sizes) == null ? void 0 : _c.size80}`,
          `${(_d = theme.sizes) == null ? void 0 : _d.size120}`
        ],
        paddingBottom: ["spaceM", "spaceXl", "spaceXxl"]
      },
      responsiveVariants: [
        {
          id: "dialog-content-size",
          prop: ["root", ...toArray(customContext.size)],
          variants: styles.contentContainer
        },
        {
          id: "dialog-content-sticky",
          prop: customContext.isSticky,
          variants: {
            true: {
              width: "100%",
              paddingBottom: "spaceNone"
            },
            false: {}
          }
        }
      ]
    },
    props: {
      variant: (_e = customContext.variant) != null ? _e : "default",
      isSticky: (_f = customContext.isSticky) != null ? _f : false
    }
  });
  const animationFromBottom = useActiveBreakpointValue(
    toArray(customContext.isSticky)
  );
  const animateContent = React3.useMemo(() => {
    return customContext.isContentAnimatable ? {
      variants: animationFromBottom ? animations.contentFromBottom : animations.content,
      initial: "hidden",
      animate: "visible",
      exit: "hidden"
    } : {};
  }, [customContext.isContentAnimatable, animationFromBottom]);
  React3.useEffect(() => {
    if ((ctxState == null ? void 0 : ctxState.open) && !!navigator.userAgent.match(/Version\/[\d\.]+.*Safari/)) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "auto";
      };
    }
    return;
  }, [ctxState == null ? void 0 : ctxState.open]);
  return /* @__PURE__ */ React3.createElement(
    DialogBody,
    {
      preventBodyScroll: false,
      "aria-label": customContext.a11yLabel,
      hideOnInteractOutside: customContext.hideOnOverlayClick,
      ...omittedCustomContext,
      ...rest,
      render: (dialogProps) => /* @__PURE__ */ React3.createElement(DialogOverlay, null, /* @__PURE__ */ React3.createElement(Container, { element: "DIALOG_CONTAINER", sx: DIALOG_CONTAINER.styles }, (ctxState == null ? void 0 : ctxState.open) && /**
       * Add the props for the animation if its set to true
       */
      /* @__PURE__ */ React3.createElement(
        Motion2,
        {
          ...animateContent,
          element: "DIALOG_CONTENT",
          __root: DIALOG_CONTENT.styles,
          ...dialogProps
        },
        /* @__PURE__ */ React3.createElement(MotionContext.Provider, { value: {} }, children)
      )))
    }
  );
};

// src/primitives/disclosure.tsx
import * as React4 from "react";
import { DialogDisclosure as BaseDialogDisclosure } from "@stepstone-genesis/ariakit-library";
var DialogDisclosure = ({ children }) => {
  return /* @__PURE__ */ React4.createElement(BaseDialogDisclosure, { render: children });
};

// src/primitives/dismiss.tsx
import * as React5 from "react";
import { systemForwardRef, useMergeStyles } from "@stepstone-genesis/system";
import { Button } from "@stepstone-genesis/components-button";
import { RemoveIcon } from "@stepstone-genesis/icons";
var DialogDismissButton = systemForwardRef(
  function DialogDismissButton2({
    children,
    isDismissible,
    onDismiss,
    a11yLabel = "dismiss dialog",
    sx,
    ...props
  }, ref) {
    const customContext = useCustomDialogContext();
    const dismissStyles = useMergeStyles(
      [styles.dismissButton, sx ? sx : {}],
      [sx]
    );
    return (isDismissible != null ? isDismissible : customContext.isDismissible) ? /* @__PURE__ */ React5.createElement(
      Button,
      {
        a11yLabel,
        ref,
        onClick: onDismiss != null ? onDismiss : customContext == null ? void 0 : customContext.onDismiss,
        sx: dismissStyles,
        variant: "linkSecondary",
        ...props
      },
      children != null ? children : /* @__PURE__ */ React5.createElement(RemoveIcon, { size: "large" })
    ) : null;
  }
);

// src/primitives/container.tsx
import { CardContent } from "@stepstone-genesis/components-card";

// src/dialog.tsx
import * as React6 from "react";
import { useStyleConfig as useStyleConfig2 } from "@stepstone-genesis/system";
import { Card } from "@stepstone-genesis/components-card";
import { Flex, FlexItem } from "@stepstone-genesis/components-flex";
var Dialog = ({
  children,
  disclosure,
  a11yLabel = "dialog",
  isSticky = false,
  ...dialogProps
}) => {
  const { DIALOG_CARD } = useStyleConfig2("DIALOG_CARD", {
    styles: {
      base: {
        boxShadow: "boxShadowElevationM",
        backgroundColor: "colorWhite",
        overflow: "visible"
      },
      responsiveVariants: [
        {
          id: "dialog-card-sticky",
          prop: isSticky,
          variants: {
            true: {
              borderBottomLeftRadius: "radiusNone",
              borderBottomRightRadius: "radiusNone"
            },
            false: {
              borderBottomLeftRadius: "radiusM",
              borderBottomRightRadius: "radiusM"
            }
          }
        }
      ]
    }
  });
  return /* @__PURE__ */ React6.createElement(DialogProvider, { a11yLabel, isSticky, ...dialogProps }, /* @__PURE__ */ React6.createElement(DialogDisclosure, null, disclosure), /* @__PURE__ */ React6.createElement(DialogContent, null, /* @__PURE__ */ React6.createElement(Flex, { flxDirection: "column", flxItems: "flex-end" }, /* @__PURE__ */ React6.createElement(FlexItem, { flxOrder: 1, sx: { width: "100%" } }, /* @__PURE__ */ React6.createElement(
    Card,
    {
      as: "div",
      __disable_card_group_ctx: true,
      sx: DIALOG_CARD.styles,
      element: "DIALOG_CARD"
    },
    children
  )), /* @__PURE__ */ React6.createElement(
    FlexItem,
    {
      flxOrder: 0,
      element: "DIALOG_DISMISS",
      sx: { position: "absolute", right: 0 }
    },
    /* @__PURE__ */ React6.createElement(DialogDismissButton, { a11yLabel: "dismiss dialog" })
  ))));
};
export {
  Dialog,
  CardContent as DialogContainer,
  DialogContent,
  DialogDisclosure,
  DialogDismissButton,
  DialogOverlay,
  DialogProvider,
  animations,
  useCustomDialogContext,
  useDialog,
  useDialogContext
};
